import { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { BarChart3, Bell, Bot, CalendarDays, ChevronDown, History, LifeBuoy, LineChart, LogOut, Menu as MenuIcon, Radio, Scale, Settings, Shield, Swords, Trophy, UserPlus, UserRound, UsersRound } from 'lucide-react'
import { Logo } from '../components/brand/Logo.jsx'
import { SearchPalette } from '../components/search/SearchPalette.jsx'
import { Avatar } from '../components/ui/Avatar.jsx'
import { Button } from '../components/ui/Button.jsx'
import { Dialog } from '../components/ui/Dialog.jsx'
import { Menu } from '../components/ui/Menu.jsx'
import { Container, Skeleton } from '../components/ui/primitives.jsx'
import { cn } from '../lib/format.js'
import { useSession } from '../lib/session.jsx'

// Events, Players and Drafting open small menus on wide screens: Events has
// events, the calendar and the competitive history; Players has the players,
// the competitive leaderboard, pred.gg player stats and your live game (for
// platform admins), comparing players and draft stats; Scrims has the scrim
// board and teams looking for a sub; Drafting has the
// draft demo and practice drafts. In the phone menu, an item shows its
// drawerLabel when it has one, since there's no menu name above it there.
export const NAV_LINKS = [
  {
    to: '/events',
    label: 'Events',
    menu: [
      { to: '/events', label: 'Browse events', drawerLabel: 'Events', icon: Trophy },
      { to: '/calendar', label: 'Calendar', icon: CalendarDays },
      { to: '/history', label: 'Past events', icon: History },
    ],
  },
  { to: '/teams', label: 'Teams' },
  {
    to: '/players',
    label: 'Players',
    menu: [
      { to: '/players', label: 'Browse players', drawerLabel: 'Players', icon: UserRound },
      { to: '/leaderboard', label: 'Competitive leaderboard', icon: Trophy },
      { to: '/stats/players', label: 'Player stats', icon: LineChart, adminOnly: true },
      { to: '/stats/live', label: 'Live game', icon: Radio, adminOnly: true },
      { to: '/compare', label: 'Compare players', icon: Scale },
      { to: '/stats', label: 'Draft stats', icon: BarChart3 },
    ],
  },
  {
    to: '/scrims',
    label: 'Scrims',
    menu: [
      { to: '/scrims', label: 'Scrim board', drawerLabel: 'Scrims', icon: Swords },
      { to: '/subs', label: 'Subs needed', icon: UserPlus },
    ],
  },
  {
    to: '/draft-demo',
    label: 'Drafting',
    menu: [
      { to: '/draft-demo', label: 'Demo', drawerLabel: 'Draft demo', icon: Bot },
      { to: '/practice', label: 'Practice draft', drawerLabel: 'Practice drafts', icon: Swords },
    ],
  },
  { to: '/highlights', label: 'Highlights' },
  { to: '/free-agents', label: 'Free agents' },
  { to: '/organizers', label: 'Organizers' },
]

function signInHref(location) {
  const next = location.pathname + location.search
  return next === '/' || next.startsWith('/signin') ? '/signin' : `/signin?next=${encodeURIComponent(next)}`
}

// Menu items for who's signed in: admin-only ones only for platform admins.
const visibleTo = (user) => (item) => !item.adminOnly || user?.isPlatformAdmin

export function profileHref(user) {
  return user.profile ? `/p/${user.profile.handle}` : '/welcome'
}

function NotificationBell({ count }) {
  const label = count ? `Notifications, ${count} unread` : 'Notifications'
  return (
    <Link
      to="/notifications"
      aria-label={label}
      className="relative grid size-9 place-items-center rounded-full text-ink-muted hover:bg-white/5 hover:text-ink"
    >
      <Bell aria-hidden="true" className="size-5" />
      {count > 0 && (
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-ink-dark"
        >
          {count > 9 ? '9+' : count}
        </span>
      )}
    </Link>
  )
}

function UserMenu({ user, onSignOut }) {
  const items = [
    { label: user.profile ? 'Your profile' : 'Set up your profile', icon: UserRound, to: profileHref(user) },
    { label: 'Your teams', icon: UsersRound, to: '/teams' },
    { label: 'Settings', icon: Settings, to: '/settings/profile' },
    { label: 'Help and support', icon: LifeBuoy, to: '/support/requests' },
    ...(user.isPlatformAdmin ? [{ label: 'Admin', icon: Shield, to: '/admin' }] : []),
    { label: 'Sign out', icon: LogOut, onSelect: onSignOut, danger: true },
  ]
  return (
    <Menu
      label={`Account menu for ${user.displayName}`}
      trigger={<Avatar src={user.avatarUrl} name={user.displayName} decorative />}
      header={
        <div>
          <p className="truncate text-sm font-medium">{user.displayName}</p>
          <p className="text-xs text-ink-muted">{user.isPlatformAdmin ? 'Platform admin' : 'Signed in with Discord'}</p>
        </div>
      }
      items={items}
    />
  )
}

export function SiteNav() {
  const { user, loading, signOut } = useSession()
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <header className="sticky top-[env(safe-area-inset-top)] z-40 border-b border-hairline bg-page/75 backdrop-blur-md">
      <Container className="flex h-16 items-center gap-4">
        <Link to="/" className="rounded-lg" aria-label="Pred Hub home">
          <Logo />
        </Link>

        {/* Eight links need the room; narrower screens use the menu. */}
        <nav aria-label="Main" className="ml-4 hidden items-center xl:flex">
          {NAV_LINKS.map((link) =>
            link.menu ? (
              <Menu
                key={link.to}
                label={link.label}
                align="start"
                items={link.menu.filter(visibleTo(user))}
                triggerClassName="rounded-lg"
                trigger={
                  <span
                    data-active={link.menu.some((item) => location.pathname.startsWith(item.to)) ? '' : undefined}
                    className="nav-sweep flex items-center gap-1 px-3 py-2 text-sm font-medium whitespace-nowrap text-ink-muted transition-colors hover:text-ink data-[active]:text-ink"
                  >
                    {link.label}
                    <ChevronDown aria-hidden="true" className="size-3.5" />
                  </span>
                }
              />
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                className="nav-sweep rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap text-ink-muted transition-colors hover:text-ink aria-[current=page]:text-ink"
              >
                {link.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <SearchPalette />
          {loading ? (
            <Skeleton className="size-9 rounded-full" />
          ) : user ? (
            <>
              <NotificationBell count={user.unreadNotifications ?? 0} />
              <UserMenu user={user} onSignOut={signOut} />
            </>
          ) : (
            <Button as={Link} to={signInHref(location)} size="sm" className="hidden sm:inline-flex">
              Sign in
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="xl:hidden"
            aria-label="Open menu"
            aria-haspopup="dialog"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon aria-hidden="true" className="size-5" />
          </Button>
        </div>
      </Container>

      <Dialog open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Menu" variant="drawer">
        <nav aria-label="Mobile" className="flex flex-col gap-1">
          {NAV_LINKS.flatMap((link) => link.menu?.filter(visibleTo(user)).map((item) => ({ to: item.to, label: item.drawerLabel ?? item.label })) ?? [link]).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) =>
                cn(
                  'rounded-xl px-4 py-3 font-display text-lg tracking-wide uppercase',
                  isActive ? 'bg-primary-500/10 text-primary-300' : 'text-ink hover:bg-white/5',
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
        {user && (
          <nav aria-label="Account" className="mt-4 flex flex-col gap-1 border-t border-hairline pt-4">
            {[
              [profileHref(user), user.profile ? 'Your profile' : 'Set up your profile'],
              ['/notifications', `Notifications${user.unreadNotifications ? ` (${user.unreadNotifications})` : ''}`],
              ['/settings/profile', 'Settings'],
              ['/support/requests', 'Help and support'],
            ].map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setDrawerOpen(false)} className="rounded-xl px-4 py-2.5 text-sm text-ink hover:bg-white/5">
                {label}
              </Link>
            ))}
          </nav>
        )}
        <div className="mt-6 border-t border-hairline pt-6">
          {user ? (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => {
                setDrawerOpen(false)
                signOut()
              }}
            >
              <LogOut aria-hidden="true" className="size-4" /> Sign out
            </Button>
          ) : (
            <Button as={Link} to={signInHref(location)} className="w-full" onClick={() => setDrawerOpen(false)}>
              Sign in
            </Button>
          )}
        </div>
      </Dialog>
    </header>
  )
}
